import {
  createPublicationEngine,
} from "../../engines/PublicationEngine";

import {
  listStudioDocuments,
} from "../../services/studioApi";

import {
  fetchCurrentStudioPublication,
  fetchStudioPublication,
  listStudioPublications,
  publishStudioLayoutVersion,
  unpublishCurrentStudioPublication,
} from "../../services/studioPublicationApi";

import {
  fetchStudioLayoutVersion,
} from "../../services/studioLayoutVersionApi";

import {
  createStudioPublicationHttpRepository,
} from "../../services/studioPublicationHttpRepository";


/**
 * Production infrastructure factory for Retail POS publication
 * resolution.
 *
 * It binds the generic PublicationEngine to authoritative HTTP
 * Studio endpoints while keeping Company + Activity scope
 * explicit.
 *
 * Route ownership is deliberately excluded.
 */
export function createRetailPOSProductionInfrastructure({
  publicationApi = {},
  layoutVersionApi = {},
  studioDocumentApi = {},
} = {}) {
  const resolvedPublicationApi = {
    fetchCurrentPublication:
      publicationApi.fetchCurrentPublication
      ?? fetchCurrentStudioPublication,

    fetchPublication:
      publicationApi.fetchPublication
      ?? fetchStudioPublication,

    listPublications:
      publicationApi.listPublications
      ?? listStudioPublications,

    publishPublication:
      publicationApi.publishPublication
      ?? publishStudioLayoutVersion,

    unpublishCurrentPublication:
      publicationApi.unpublishCurrentPublication
      ?? unpublishCurrentStudioPublication,
  };

  const resolvedFetchLayoutVersion =
    layoutVersionApi.fetchVersion
    ?? fetchStudioLayoutVersion;

  const resolvedListDocuments =
    studioDocumentApi.listDocuments
    ?? listStudioDocuments;


  const publicationRepository =
    createStudioPublicationHttpRepository({
      ...resolvedPublicationApi,
    });


  async function getVersion(
    versionId,
    {
      context,
    } = {}
  ) {
    const scope =
      extractProductionScope(
        context
      );

    const response =
      await resolvedFetchLayoutVersion(
        versionId,
        scope
      );

    return extractLayoutVersion(
      response
    );
  }


  const publicationEngine =
    createPublicationEngine({
      repository:
        publicationRepository,

      getVersion,
    });


  async function listDocuments(
    scope
  ) {
    const normalizedScope =
      extractProductionScope(
        scope
      );

    return resolvedListDocuments(
      normalizedScope
    );
  }


  return Object.freeze({
    publicationRepository,
    publicationEngine,
    getVersion,
    listDocuments,
  });
}


function extractProductionScope(
  source
) {
  if (
    source === null
    || typeof source !== "object"
    || Array.isArray(source)
  ) {
    throw new TypeError(
      "Retail POS production infrastructure requires an explicit scope."
    );
  }

  return Object.freeze({
    companyUuid:
      normalizeRequiredString(
        source.companyUuid,
        "companyUuid"
      ),

    activityUuid:
      normalizeRequiredString(
        source.activityUuid,
        "activityUuid"
      ),
  });
}


function extractLayoutVersion(
  response
) {
  const body =
    response
    && typeof response === "object"
    && !Array.isArray(response)
    && Object.prototype.hasOwnProperty.call(
      response,
      "data"
    )
      ? response.data
      : response;

  const version =
    body?.version
    ?? null;

  if (
    version === null
    || typeof version !== "object"
    || Array.isArray(version)
  ) {
    throw new TypeError(
      "Studio LayoutVersion API response must contain version."
    );
  }

  return version;
}


function normalizeRequiredString(
  value,
  name
) {
  if (
    typeof value !== "string"
    || !value.trim()
  ) {
    throw new TypeError(
      `${name} must be a non-empty string.`
    );
  }

  return value.trim();
}


export default
  createRetailPOSProductionInfrastructure;
